import React, { useState } from 'react'
import { Redirect } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { endOfDay, startOfDay } from 'date-fns'
import styled from 'styled-components'
import GdprImage from 'assets/GDPR.png'
import Icon from 'components/Icon'
import Button from 'components/Button'
import ConfirmModal from 'components/ConfirmModal'
import FullPageLoading from 'components/FullPageLoading'
import { addModal } from 'features/modals/modalSlice'
import { eventSelector, getEventData } from 'features/events/eventSlice'
import { showNotification } from 'features/notifications'
import { showErrorNotification } from 'features/notifications/notificationsSlice'
import { useEventSlug } from 'hooks'
import { colors, _ } from 'utils'
import { removeUserDataFromEvent } from './graphql/mutation'

const S = {
    Wrapper: styled.div`
        display: flex;
        width: 100%;
        overflow: hidden;
        background: #FFFFFF;
        box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.2);
        border-radius: 4px;
    `,
    Content: styled.div`
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        flex-grow: 1;
        padding: 20px;
        font-weight: 300;
        font-size: 14px;
        line-height: 18px;
        color: ${colors.darkOpacity(0.9)};
    `,
    ImageWrapper: styled.div`
        flex-shrink: 0;
        padding: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: ${colors.pageBackground};
    `,
    Image: styled.img`
        width: 80px;
        height: auto;
        object-fit: contain;
        object-position: center;
    `,
    GdprCompliance: styled.div`
        margin: -20px;
        margin-top: 0;
        padding: 18px;
        border-top: 1px solid ${colors.statusBorder};
        background-color: ${colors.primaryOpacity(0.02)};
        font-size: 14px;
        color: ${colors.grayTitle};
    `
}

const GdprPage = () => {
    const dispatch = useDispatch()
    const [isUploading, setIsUploading] = useState(false)
    const eventSlug = useEventSlug()
    const eventData = useSelector(eventSelector)
    if (startOfDay(new Date()) < endOfDay(new Date(eventData?.endDate))) {
        return <Redirect to={`/${eventSlug}`} />
    }

    const handleRemoveUserData = async () => {
        setIsUploading(true)
        const response = await removeUserDataFromEvent(eventSlug)
        setIsUploading(false)
        if (response.errors.length) {
            return dispatch(showErrorNotification(response.errors))
        }
        dispatch(showNotification({
            body: _('gdpr.apply_success'),
            type: 'success',
            displayDuration: 3000
        }))
        dispatch(getEventData(eventSlug))
    }

    const showConfirm = () => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <ConfirmModal
                    close={close}
                    title={_('gdpr.apply_modal_title')}
                    confirmDescription={_('gdpr.apply_modal_description')}
                    variantConfirm="primary"
                    onConfirm={handleRemoveUserData}
                    confirmText={_('gdpr.apply_modal_apply_button')}
                />
            ),
            shouldNotWrap: true
        }))
    }

    return (
        <S.Wrapper>
            <S.Content>
                <p>{_('gdpr.description')}</p>
                {eventData?.userDataIsDeleted ? (
                    <S.GdprCompliance className="flex-align-center">
                        <Icon icon="checkCircle" color={colors.success} className="mr-2" />
                        {_('gdpr.compliance_applied')}
                    </S.GdprCompliance>
                ) : (
                    <Button variant="danger" style={{ alignSelf: 'baseline' }} onClick={showConfirm}>
                        {_('gdpr.remove_button')}
                    </Button>
                )}
            </S.Content>
            {!eventData?.userDataIsDeleted && (
                <S.ImageWrapper>
                    <S.Image src={GdprImage} />
                </S.ImageWrapper>
            )}
            {isUploading && <FullPageLoading isAbsolute />}
        </S.Wrapper>
    )
}

export default GdprPage
